/*
 * File: ClinicalToolbar.js
 * Project: mp-mobile-app
 *
 * Created by Vamsi Surreddy
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Controls the clinical widget toolbar
 */
import React, { ChangeEventHandler } from "react";
import {
  Toolbar,
  InputAdornment,
  Theme,
  createStyles
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import CancelIcon from "@material-ui/icons/CancelRounded";
import ClinicalStore from "./Store";
import RefreshIcon from "@material-ui/icons/Refresh";
import MainHeader from "components/MainHeader";
import { StoreContext } from "utils/useStore";
import { WithStyles } from "@material-ui/core";
import ClinicalEditButton from "./ClinicalEditButton";

const styles = (theme: Theme) =>
  createStyles({
    backButton: {
      padding: 0
    },
    grow: {
      flexGrow: 1
    },
    toolbar: {
      padding: theme.spacing(2.5),
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1)
    },
    clinicalHeader: {
      display: "block"
    },
    icon: {
      color: theme.palette.common.icons
    },
    search: {
      position: "relative",
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.common.popupbg,
      marginLeft: 0,
      width: "100%",
      [theme.breakpoints.up("xs")]: {
        marginLeft: theme.spacing(1),
        width: "auto"
      }
    },
    searchIcon: {
      width: theme.spacing(5),
      height: "100%",
      position: "absolute",
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    inputRoot: {
      color: "inherit",
      width: "100%"
    },
    inputInput: {
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(4),
      transition: theme.transitions.create("width"),
      [theme.breakpoints.up("xs")]: {
        width: 80,
        "&:focus": {
          width: 120
        }
      },
      [theme.breakpoints.up("sm")]: {
        width: 200,
        "&:focus": {
          width: 300
        }
      }
    }
  });

type Props = WithStyles<typeof styles>;

class ClinicalToolbar extends React.Component<Props> {
  static contextType = StoreContext;

  context!: React.ContextType<typeof StoreContext>;

  search: HTMLInputElement | undefined;

  get store() {
    if (this.context === undefined) {
      throw new Error("Missing context in ClinicalToolbar");
    }
    return this.context.getStore(ClinicalStore);
  }

  handleInput: ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = event => {
    const element = document.getElementById("clearable");
    if (element !== null) {
      if (event.target.value) {
        element.style.display = "flex";
      } else {
        element.style.display = "none";
      }
    }

    this.store.setKeyword(event.target.value.trim().toLowerCase());
  };

  componentDidMount() {
    const element = document.getElementById("clearable");
    if (element !== null) {
      if (this.store.keyword) {
        element.style.display = "flex";
      } else {
        element.style.display = "none";
      }
    }
  }

  UNSAFE_componentWillMount() {
    this.store.setKeyword("");
  }

  clear = () => {
    if (this.search !== undefined) {
      this.search.value = "";
    }
    this.store.setKeyword("");
    const element = document.getElementById("clearable");
    if (element !== null) {
      element.style.display = "none";
    }
  };

  doRefresh = () => {
    this.store.fetchClinicalCounts();
  };

  render() {
    const { classes } = this.props;
    return (
      <React.Fragment>
        <Toolbar className={classes.toolbar}>
          <div className={classes.clinicalHeader}>
            <MainHeader pageTitle={"Clinical"} pageSubtitle={"Patient"} />
          </div>
          <div style={{ flexGrow: 1 }} />
          <div className={classes.search} id="clinicalSearch">
            <div className={classes.searchIcon}>
              <SearchIcon />
            </div>
            <InputBase
              placeholder="Filter..."
              onChange={this.handleInput}
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput
              }}
              endAdornment={
                <InputAdornment position="start" id="clearable">
                  <CancelIcon onClick={this.clear} />
                </InputAdornment>
              }
              inputRef={el => (this.search = el)}
            />
          </div>
          <IconButton onClick={this.doRefresh}>
            <RefreshIcon className={classes.icon} />
          </IconButton>
          <ClinicalEditButton />
        </Toolbar>
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(ClinicalToolbar);
